import { useEffect, useRef } from 'react';
import { useGame, ITEM_NAMES } from '../state/useGame';
import { SKILLS_BY_ID } from '../data';
import type { SkillId } from '../domain/state';

function formatDuration(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours >= 1) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (totalMinutes >= 1) return `${totalMinutes}m`;
  return `${Math.floor(ms / 1_000)}s`;
}

function skillName(id: SkillId): string {
  return SKILLS_BY_ID.get(id)?.name ?? id;
}

/**
 * Blocking offline report (Phase 2 contract): shown when the elapsed gap is
 * ≥ 5 minutes. role=dialog + aria-modal + aria-describedby, dismiss button
 * takes focus on open, Tab/Shift+Tab are trapped inside the dialog, Escape
 * closes, and focus returns to the previously focused element on dismiss.
 */
export function OfflineSummaryModal() {
  const summary = useGame((s) => s.offlineSummary);
  const dismiss = useGame((s) => s.dismissOfflineSummary);
  const dismissRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!summary) return;
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dismissRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [summary]);

  useEffect(() => {
    if (!summary) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dismiss();
        return;
      }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (!dialog.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [summary, dismiss]);

  if (!summary) return null;

  const xpEntries = Object.entries(summary.xpGained) as Array<[SkillId, number]>;
  const remainderSec = Math.round(summary.remainderMs / 1_000);

  return (
    <div className="modal-backdrop">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="offline-summary-title"
        aria-describedby="offline-summary-report"
        className="modal card"
      >
        <h2 id="offline-summary-title" style={{ margin: '0 0 12px', color: 'var(--accent-nous)' }}>
          Night watch report
        </h2>
        <dl id="offline-summary-report" className="offline-report">
          <div>
            <dt>Time away</dt>
            <dd>{formatDuration(summary.elapsedMs)}</dd>
          </div>
          <div>
            <dt>Simulated</dt>
            <dd>
              {formatDuration(summary.simulatedMs)}
              {summary.capped && <span className="tag">capped at 24h</span>}
            </dd>
          </div>
          <div>
            <dt>Action</dt>
            <dd>
              {summary.actionLabel} — {summary.ticks} completed intervals
              {remainderSec > 0 ? `, ${remainderSec}s toward the next` : ''}
            </dd>
          </div>
          {xpEntries.length > 0 && (
            <div>
              <dt>XP gained</dt>
              <dd>
                {xpEntries
                  .map(([id, xp]) => `${skillName(id)} +${xp.toLocaleString('en-US')}`)
                  .join(' · ')}
              </dd>
            </div>
          )}
          {summary.levelGains.length > 0 && (
            <div>
              <dt>Levels</dt>
              <dd>
                {summary.levelGains
                  .map((g) => `${skillName(g.skillId)} ${g.from} → ${g.to}`)
                  .join(' · ')}
              </dd>
            </div>
          )}
          {summary.itemDeltas.length > 0 && (
            <div>
              <dt>Items</dt>
              <dd>
                {summary.itemDeltas
                  .map((d) => `${ITEM_NAMES[d.itemId]} ${d.delta > 0 ? '+' : ''}${d.delta}`)
                  .join(' · ')}
              </dd>
            </div>
          )}
          {summary.notableDrops.length > 0 && (
            <div>
              <dt>Notable drops</dt>
              <dd>{summary.notableDrops.map((id) => ITEM_NAMES[id]).join(' · ')}</dd>
            </div>
          )}
          {summary.lostItems.length > 0 && (
            <div>
              <dt>Lost drops</dt>
              <dd>
                {summary.lostItems
                  .map((l) => `${ITEM_NAMES[l.itemId]} ×${l.qty}`)
                  .join(' · ')}{' '}
                — bank and inventory were full
              </dd>
            </div>
          )}
        </dl>
        <button ref={dismissRef} className="primary modal-dismiss" onClick={dismiss}>
          Continue the grind
        </button>
      </div>
    </div>
  );
}
